import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { signOut } from "firebase/auth";
import { auth, db } from "../firebase";
import { collection, getDocs } from "firebase/firestore";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import "../App.css"; // Ensure CSS is updated
import logo from "../assets/logo.png";
import dp from "../assets/dp.png";
import dashboardIcon from "../assets/layout-grid.png";
import appointmentsIcon from "../assets/Tick Square.png";
import logoutIcon from "../assets/logout.png";
import userIcon from "../assets/user_avatar.png";
ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const Dashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState([
    { title: "Total Patient", value: 0, icon: "🧑‍⚕️" },
    { title: "No. Appointment", value: 0, icon: "📅" },
    { title: "Number of Delays", value: 0, icon: "⏳" },
    { title: "Total Doctors", value: 0, icon: "🩺" },
  ]);
  const [patientVisitData, setPatientVisitData] = useState({
    labels: ["<15", "15-24", "25-44", "45-64", "64-75", ">75"],
    datasets: [
      { label: "Male", backgroundColor: "#4285F4", data: [0, 0, 0, 0, 0, 0] },
      { label: "Female", backgroundColor: "#34A853", data: [0, 0, 0, 0, 0, 0] },
    ],
  });
  const [minutesDelayedData, setMinutesDelayedData] = useState({
    labels: ["<10", "10-15", "15-30", "30-45", "45-60", ">60"],
    datasets: [
      { label: "Delays", backgroundColor: "#4285F4", data: [0, 0, 0, 0, 0, 0] },
    ],
  });
  const [delayByDoctorData, setDelayByDoctorData] = useState({
    labels: [],
    datasets: [
      { label: "Delays", backgroundColor: "#FF5733", data: [] },
    ],
  });
  const [topDoctors, setTopDoctors] = useState([]);

  const handleLogout = async () => {
    try {
      await signOut(auth);
      localStorage.removeItem("token");
      navigate("/login");
    } catch (error) {
      console.error("Error signing out:", error);
      // Still clear local storage and navigate even if signOut fails
      localStorage.removeItem("token");
      navigate("/login");
    }
  };

  // Calculate age from date of birth or age field
  const calculateAge = (dob, age) => {
    if (age) return age;
    if (dob) {
      const birthDate = new Date(dob);
      const today = new Date();
      let calculatedAge = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();
      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        calculatedAge--;
      }
      return calculatedAge;
    }
    return null;
  };

  // Get age group index
  const getAgeGroup = (age) => {
    if (!age) return -1;
    if (age < 15) return 0;
    if (age < 25) return 1;
    if (age < 45) return 2;
    if (age < 65) return 3;
    if (age < 75) return 4;
    return 5;
  };

  // Get delay bucket index
  const getDelayBucket = (delayMinutes) => {
    if (delayMinutes < 10) return 0;
    if (delayMinutes < 15) return 1;
    if (delayMinutes < 30) return 2;
    if (delayMinutes < 45) return 3;
    if (delayMinutes < 60) return 4;
    return 5;
  };

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);

        // Fetch all collections in parallel
        const [usersSnapshot, doctorsSnapshot, appointmentsSnapshot] = await Promise.all([
          getDocs(collection(db, "users")),
          getDocs(collection(db, "doctors")),
          getDocs(collection(db, "appointments")).catch(() => ({ empty: true, docs: [] })),
        ]);

        // Calculate stats
        const totalPatients = usersSnapshot.size;
        const totalDoctors = doctorsSnapshot.size;
        const appointments = appointmentsSnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        const totalAppointments = appointments.length;
        const delayedAppointments = appointments.filter(
          (apt) => apt.delayTime && apt.delayTime > 0
        ).length;

        // Update stats
        setStats([
          { title: "Total Patient", value: totalPatients, icon: "🧑‍⚕️" },
          { title: "No. Appointment", value: totalAppointments, icon: "📅" },
          { title: "Number of Delays", value: delayedAppointments, icon: "⏳" },
          { title: "Total Doctors", value: totalDoctors, icon: "🩺" },
        ]);

        // Process patient visit data by age and gender
        const ageGroups = {
          male: [0, 0, 0, 0, 0, 0],
          female: [0, 0, 0, 0, 0, 0],
        };

        usersSnapshot.forEach((doc) => {
          const userData = doc.data();
          const age = calculateAge(userData.dateOfBirth, userData.age);
          const gender = userData.gender?.toLowerCase() || "unknown";
          const ageGroupIndex = getAgeGroup(age);

          if (ageGroupIndex >= 0) {
            if (gender === "male" || gender === "m") {
              ageGroups.male[ageGroupIndex]++;
            } else if (gender === "female" || gender === "f") {
              ageGroups.female[ageGroupIndex]++;
            }
          }
        });

        setPatientVisitData({
          labels: ["<15", "15-24", "25-44", "45-64", "64-75", ">75"],
          datasets: [
            { label: "Male", backgroundColor: "#4285F4", data: ageGroups.male },
            { label: "Female", backgroundColor: "#34A853", data: ageGroups.female },
          ],
        });

        // Process minutes delayed data
        const delayBuckets = [0, 0, 0, 0, 0, 0];
        appointments.forEach((apt) => {
          const delayMinutes = apt.delayTime || 0;
          if (delayMinutes > 0) {
            const bucketIndex = getDelayBucket(delayMinutes);
            delayBuckets[bucketIndex]++;
          }
        });

        setMinutesDelayedData({
          labels: ["<10", "10-15", "15-30", "30-45", "45-60", ">60"],
          datasets: [
            { label: "Delays", backgroundColor: "#4285F4", data: delayBuckets },
          ],
        });

        // Process delay by doctor
        const doctorDelays = {};
        appointments.forEach((apt) => {
          const doctorName = apt.physician || apt.doctorName || "Unknown";
          const delayMinutes = apt.delayTime || 0;
          if (!doctorDelays[doctorName]) {
            doctorDelays[doctorName] = { total: 0, count: 0 };
          }
          doctorDelays[doctorName].total += delayMinutes;
          doctorDelays[doctorName].count++;
        });

        const doctorNames = Object.keys(doctorDelays).slice(0, 10); // Top 10 doctors
        const doctorDelayData = doctorNames.map((name) => {
          const avgDelay = doctorDelays[name].total / doctorDelays[name].count;
          return Math.round(avgDelay);
        });

        setDelayByDoctorData({
          labels: doctorNames,
          datasets: [
            { label: "Avg Delay (mins)", backgroundColor: "#FF5733", data: doctorDelayData },
          ],
        });

        // Calculate top doctors by on-time appointments
        const doctorStats = {};
        appointments.forEach((apt) => {
          const doctorName = apt.physician || apt.doctorName || "Unknown";
          if (!doctorStats[doctorName]) {
            doctorStats[doctorName] = { onTime: 0, total: 0 };
          }
          doctorStats[doctorName].total++;
          if (!apt.delayTime || apt.delayTime === 0) {
            doctorStats[doctorName].onTime++;
          }
        });

        // Get doctor specialties from doctors collection and count patients
        const doctorsMap = {};
        const doctorPatientCounts = {};
        doctorsSnapshot.forEach((doc) => {
          const doctorData = doc.data();
          doctorsMap[doctorData.name] = doctorData.specialty || "General";
          doctorPatientCounts[doctorData.name] = 0;
        });

        // Count patients per doctor
        usersSnapshot.forEach((doc) => {
          const userData = doc.data();
          const doctorName = userData.doctorName;
          if (doctorName && doctorPatientCounts.hasOwnProperty(doctorName)) {
            doctorPatientCounts[doctorName]++;
          }
        });

        const topDoctorsList = Object.entries(doctorStats)
          .map(([name, stats]) => ({
            name,
            specialty: doctorsMap[name] || "General",
            score: stats.total > 0 ? `${Math.round((stats.onTime / stats.total) * 100)}%` : "0%",
            onTimeRate: stats.total > 0 ? stats.onTime / stats.total : 0,
            patientCount: doctorPatientCounts[name] || 0,
          }))
          .sort((a, b) => b.onTimeRate - a.onTimeRate)
          .slice(0, 10);

        // Also include doctors with no appointments but have patients
        const doctorsWithPatients = Object.entries(doctorPatientCounts)
          .filter(([name, count]) => count > 0 && !doctorStats[name])
          .map(([name, count]) => ({
            name,
            specialty: doctorsMap[name] || "General",
            score: "N/A",
            onTimeRate: 0,
            patientCount: count,
          }));

        // Combine and sort by patient count for doctors without appointments
        const allDoctors = [...topDoctorsList, ...doctorsWithPatients]
          .sort((a, b) => {
            if (a.onTimeRate === b.onTimeRate) {
              return b.patientCount - a.patientCount;
            }
            return b.onTimeRate - a.onTimeRate;
          })
          .slice(0, 10);

        setTopDoctors(allDoctors);
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, []);

  return (
    <div className="dashboard">
      <div className="sidebar">
        <img src={logo} alt="Timely Logo" className="logo-image" />
        <div className="user-profile">
          <div className="user-info">
            <h3>Kevin Beers</h3>
            <p>Support Team</p>
          </div>
        </div>
        <nav>
          <a href="/dashboard" className="active">
            <img src={dashboardIcon} alt="Dashboard" className="menu-icon" />
            Dashboard
          </a>
          <a href="/appointments">
            <img src={appointmentsIcon} alt="Appointments" className="menu-icon" />
            Appointments
          </a>
          <a href="/users">
            <img src={userIcon} alt="Users" className="menu-icon" />
            Users
          </a>
          <a href="/doctors">
            <span className="menu-icon" style={{ fontSize: "24px" }}>👨‍⚕️</span>
            Doctors
          </a>
          <a href="/" onClick={(e) => { e.preventDefault(); handleLogout(); }}>
            <img src={logoutIcon} alt="Log Out" className="menu-icon" />
            Log Out
          </a>
        </nav>
      </div>

      <div className="main-content">
        <h1 className="dashboard-title">Dashboard</h1>
        {loading ? (
          <div style={{ textAlign: "center", padding: "40px" }}>
            <p>Loading dashboard data...</p>
          </div>
        ) : (
          <>
            <div className="stats-cards">
              {stats.map((stat, index) => (
                <div key={index} className="card">
                  <span className="icon">{stat.icon}</span>
                  <h3>{stat.value}</h3>
                  <p>{stat.title}</p>
                </div>
              ))}
            </div>

        {/* Two-Column Layout */}
        <div className="dashboard-layout">
          {/* Left Column - Stacked Charts */}
          <div className="charts-column">
            <div className="chart">
              <h2>Patient Visit</h2>
              <Bar data={patientVisitData} options={{ responsive: true, maintainAspectRatio: false }} />
            </div>
            <div className="chart">
              <h2>Minutes Delayed</h2>
              <Bar data={minutesDelayedData} options={{ responsive: true, maintainAspectRatio: false }} />
            </div>
            <div className="chart">
              <h2>Delay by Doctor</h2>
              <Bar data={delayByDoctorData} options={{ responsive: true, maintainAspectRatio: false }} />
            </div>
          </div>

          {/* Right Column - Top Doctors */}
          <div className="top-doctors">
  <h2>Top 10 Doctors by On-time Apps</h2>
  <ul>
    {topDoctors.map((doctor, index) => (
      <li key={index} className="doctor-item">
        <div className="doctor-info">
     
        <img src={dp} alt={doctor.name} className="doctor-avatar" />
          <div>
            {doctor.name}
            <p className="doctor-specialty">{doctor.specialty}</p>
            {doctor.patientCount > 0 && (
              <p style={{ fontSize: "12px", color: "#666", marginTop: "4px" }}>
                {doctor.patientCount} patient{doctor.patientCount !== 1 ? "s" : ""}
              </p>
            )}
          </div>
        </div>
        <span className="doctor-score">{doctor.score}</span>
      </li>
    ))}
  </ul>
</div>

        </div>
          </>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
